import { Injectable, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, finalize } from 'rxjs';
import { DynamicFormService } from '../../../core/services/dynamic-form.service';
import {
  ConditionGroupDto,
  CreateConditionGroupRequest,
  CreateDynamicFormRequest,
  CreateSectionRequest,
  DynamicFormDetails,
  FormStatus,
  FormVisibility,
  SectionDto,
  getVisibilityLabel,
  getStatusLabel,
  ConditionConnector,
  ConditionDto
} from '../models/dynamic-form';

@Injectable()
export class FormulariosWizardStore {
  private service = inject(DynamicFormService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  // State signals
  private _id = signal<number | null>(null);
  private _title = signal<string>('');
  private _visibility = signal<FormVisibility>('Public');
  private _alwaysDisplay = signal<boolean>(false);
  private _status = signal<FormStatus>('Draft');
  private _publishedAt = signal<string | null>(null);
  
  // Using DTO types for state to simplify mapping, but we'll map to CreateRequest when saving
  private _conditionGroups = signal<ConditionGroupDto[]>([]);
  private _sections = signal<SectionDto[]>([]);

  private _currentStep = signal<number>(1);
  private _maxStepReached = signal<number>(1);
  private _loading = signal<boolean>(false);

  // Public read-only signals
  readonly id = this._id.asReadonly();
  readonly title = this._title.asReadonly();
  readonly visibility = this._visibility.asReadonly();
  readonly alwaysDisplay = this._alwaysDisplay.asReadonly();
  readonly status = this._status.asReadonly();
  readonly publishedAt = this._publishedAt.asReadonly();
  readonly conditionGroups = this._conditionGroups.asReadonly();
  readonly sections = this._sections.asReadonly();
  
  readonly currentStep = this._currentStep.asReadonly();
  readonly maxStepReached = this._maxStepReached.asReadonly();
  readonly loading = this._loading.asReadonly();

  // Computed
  readonly isEditing = computed(() => !!this._id());
  
  readonly summary = computed(() => {
    const conditionsCount = this._alwaysDisplay() 
      ? 0 
      : this._conditionGroups().reduce((acc, group) => acc + group.conditions.length, 0);

    const questionsCount = this._sections().reduce((acc, section) => acc + section.questions.length, 0);

    return {
      visibilityLabel: getVisibilityLabel(this._visibility()),
      conditionsLabel: this._alwaysDisplay() 
        ? 'Sempre exibir' 
        : `${conditionsCount} ativa${conditionsCount !== 1 ? 's' : ''}`,
      questionsCount,
      statusLabel: getStatusLabel(this._status())
    };
  });

  // Actions
  setStep(step: number) {
    if (step <= this._maxStepReached()) {
      this._currentStep.set(step);
    }
  }

  loadForm(id: number) {
    this._loading.set(true);

    // Detect current route to set step correctly
    const url = this.router.url;
    if (url.includes('/perguntas')) {
      this._currentStep.set(2);
    }

    this.service.getFormDetails(id)
      .pipe(finalize(() => this._loading.set(false)))
      .subscribe({
        next: (form) => {
          this._id.set(form.id);
          this._title.set(form.title);
          this._visibility.set(form.visibility);
          this._alwaysDisplay.set(form.alwaysDisplay);
          this._status.set(form.status);
          this._publishedAt.set(form.publishedAt);
          this._conditionGroups.set(form.conditionGroups);
          this._sections.set(form.sections);
          
          // Allow navigation to all steps when editing
          this._maxStepReached.set(2);
        },
        error: (err) => {
          console.error('Error loading form', err);
          this.router.navigate(['/formularios']);
        }
      });
  }

  updateStep1(data: { 
    title: string; 
    visibility: FormVisibility; 
    alwaysDisplay: boolean; 
    conditionGroups: ConditionGroupDto[] 
  }) {
    this._title.set(data.title);
    this._visibility.set(data.visibility);
    this._alwaysDisplay.set(data.alwaysDisplay);
    this._conditionGroups.set(data.conditionGroups);
    
    // Advance step
    if (this._currentStep() === 1) {
      this._maxStepReached.set(Math.max(this._maxStepReached(), 2));
      this._currentStep.set(2);
      this.router.navigate([
        '/formularios',
        this.isEditing() ? 'editar' : 'novo',
        ...(this.isEditing() ? [this._id()] : []),
        'perguntas',
      ]);
    }
  }

  updateStep2(data: { sections: SectionDto[] }) {
    this._sections.set(data.sections);
  }

  saveDraft() {
    this.save('Draft');
  }

  publish() {
    this.save('Published');
  }

  private save(targetStatus: FormStatus) {
    if (this._loading()) return;
    
    this._loading.set(true);
    
    const request: CreateDynamicFormRequest = {
      title: this._title(),
      visibility: this._visibility(),
      alwaysDisplay: this._alwaysDisplay(),
      conditionGroups: this.mapConditionsToRequest(this._conditionGroups()),
      sections: this.mapSectionsToRequest(this._sections())
    };

    const action: Observable<DynamicFormDetails | void> = this.isEditing()
      ? this.service.updateForm(this._id()!, request)
      : this.service.createForm(request);

    action.pipe(finalize(() => this._loading.set(false)))
      .subscribe({
        next: (res) => {
          if (targetStatus === 'Published') {
            const formId = this.isEditing() ? this._id()! : (res as DynamicFormDetails)?.id;
            if (formId) {
              this.service.publishForm(formId).subscribe({
                next: () => this.router.navigate(['/formularios']),
                error: (err) => console.error('Error publishing form', err)
              });
              return;
            }
          }
          this.router.navigate(['/formularios']);
        },
        error: (err) => console.error('Error saving form', err)
      });
  }

  // Mappers
  private mapConditionsToRequest(groups: ConditionGroupDto[]): CreateConditionGroupRequest[] {
    return groups.map(g => ({
      connector: g.connector,
      order: g.order,
      conditions: g.conditions.map(c => ({
        conditionType: c.conditionType,
        operator: c.operator,
        timeValue: c.timeValue,
        period: c.period,
        order: c.order
      }))
    }));
  }

  private mapSectionsToRequest(sections: SectionDto[]): CreateSectionRequest[] {
    return sections.map(s => ({
      title: s.title,
      order: s.order,
      questions: s.questions.map(q => ({
        text: q.text,
        order: q.order
      }))
    }));
  }
}
